import React, {
  useCallback, useState, useEffect, useMemo,
} from 'react';
import {
  isEqual, isNil, omitBy, omit, uniq,
} from 'lodash';
import { format } from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';
import { getQueryParams } from 'src/fetch/utils';
import qs from 'qs';

const defaultFormat = (params) => params;

const getSearchStringObject = (str) => str.replace('?', '').split('&').sort().map((pair) => {
  const splitted = pair.split('=');
  if (splitted.length !== 2) {
    return splitted;
  }
  return { [splitted[0]]: splitted[1] };
});

const defaultConfig = {
  isReady: true,
  useSearchQuery: false,
  formatSearchQuery: null,
  formatParamsForQuery: null,
  parentSearchParams: null,
  formatDates: false,
};

const RECYCLE_BIN_PARAM = 'deletedOnly';
const dateRegex = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}\.\d{3}Z?$/;
const queryDateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/gm;

/**
 * !!!
 * This hook is used on every page
 * Inform other teams when editing
 */

const useSearchParams = (
  // Ininital Search params - must contain all properties, including empty ones
  initialValues,
  // Method to format params values returned from their components to be used for requests
  formatParamsProp, {
    // Ready flag - if false - does not run any changes till true
    isReady = true,
    // If true - search params changes are reflected on the Query String
    useSearchQuery = false,
    // Method to format Query search params to Search Params
    formatSearchQuery,
    // Method to format Search Params to Search Query. If not provied - formatParams is used
    formatParamsForQuery,
    // Value to overwrite everything
    parentSearchParams,
    // If true - dates are formatted:
    // search query (if useSearchQuery is true) - {MM/DD/YYYY}
    // requests (formatted) - ISO String (UTC)
    // TimeZoneOffset is added to formatted
    formatDates = false,
    // Values to reset
    resetSearchParams,
  } = defaultConfig,
) => {
  const formatParams = formatParamsProp ?? defaultFormat;

  const location = useLocation();
  const history = useHistory();

  // Merge query string into initial params
  const combineParamsWithQueryString = (params) => {
    if (!useSearchQuery) {
      return params;
    }
    // Get params from Query
    const queryStringParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    const preFormat = (data) => {
      if (!formatDates) {
        return data;
      }
      // Set Dates from query values MM/DD/YYYY
      return Object.keys(data).reduce((acc, key) => {
        if (queryDateRegex.test(acc[key])) {
          const [monthStr, dayStr, yearStr] = acc[key].split('/');
          const month = Number(monthStr) - 1;
          const dateValue = new Date(Number(yearStr), month, Number(dayStr), 0, 0, 0, 0);
          return { ...acc, [key]: dateValue };
        }
        return acc;
      }, data);
    };

    const preFormatted = preFormat(queryStringParams);

    const formattedQueryParams = formatSearchQuery ? formatSearchQuery(preFormatted) : preFormatted;

    // Get all params keys from Query search and params
    const allParamsKeys = uniq([...Object.keys(formattedQueryParams), ...Object.keys(params)]);

    // Build new value
    const newParams = allParamsKeys.reduce((acc, k) => {
      if (!isEqual(params[k], formattedQueryParams[k]) && formattedQueryParams[k]) {
        return {
          ...acc,
          [k]: formattedQueryParams[k] === 'false'
            ? false : formattedQueryParams[k] === 'null' ? null : formattedQueryParams[k],
        };
      }
      return { ...acc, [k]: params[k] };
    }, {});

    return newParams;
  };

  // Raw/not formatted value
  const [searchParams, setSearchParams] = useState(combineParamsWithQueryString(parentSearchParams ?? initialValues));

  const showRecycleBin = () => setSearchParams((prev) => ({ ...prev, [RECYCLE_BIN_PARAM]: true }));
  const hideRecycleBin = () => setSearchParams((prev) => omit(prev, RECYCLE_BIN_PARAM));

  // Force search params update on initial values change
  useEffect(() => {
    if (!isReady) {
      return;
    }
    setSearchParams((prev) => {
      const currentParams = omit(prev, RECYCLE_BIN_PARAM);
      const newValue = combineParamsWithQueryString({ ...currentParams, ...initialValues });
      // Overwrite with updated initial values if not deep equal
      if (isEqual(newValue, prev)) {
        return prev;
      }
      return combineParamsWithQueryString({ ...currentParams, ...initialValues });
    });
  }, [initialValues, isReady]);

  useEffect(() => {
    if (!parentSearchParams || !isReady) {
      return;
    }
    setSearchParams(combineParamsWithQueryString(parentSearchParams));
  }, [parentSearchParams, isReady]);

  const setByKey = useCallback((key, value) => setSearchParams((prev) => ({ ...prev, [key]: value })), []);

  const set = setSearchParams;

  const reset = useCallback(() => setSearchParams(resetSearchParams ?? initialValues ?? {}), [resetSearchParams]);

  // Format Search Params to be used for requests
  const formatSearchParams = useCallback((values) => {
    const { textSearch, search, ...others } = values;
    const resultSearch = textSearch?.length > 0 && search?.length > 0 ? [...search, textSearch] : search ?? textSearch;

    const params = { ...others, search: resultSearch };

    const clean = omitBy(
      formatParams(params),
      (value) => isNil(value) || value?.length === 0,
    );

    return clean;
  }, [formatParams, formatDates]);

  // Format Search Params to be used for search query
  const formatSearchParamsForQuery = useCallback((values) => {
    if (!useSearchQuery) {
      return;
    }
    const { textSearch, search, ...others } = values;
    const resultSearch = textSearch?.length > 0 && search?.length > 0 ? [...search, textSearch] : search ?? textSearch;

    // Format Date to {YYYY-MM-DD} - to be parsed for initial values;
    // Date ranges should be formatted by formatParams and formatParamsForQuery
    const preFormat = (data) => {
      if (!formatDates) {
        return data;
      }
      return Object.keys(data).reduce((acc, key) => {
        if (acc[key] instanceof Date) {
          return { ...acc, [key]: format(acc[key], 'MM/DD/YYYY') };
        }
        if (dateRegex.test(acc[key])) {
          return { ...acc, [key]: format(new Date(acc[key]), 'MM/DD/YYYY') };
        }
        return acc;
      }, data);
    };

    const preFormatted = preFormat({ ...others, search: resultSearch });

    const formatMethod = formatParamsForQuery ?? formatParams;

    const clean = omitBy(
      formatMethod(preFormatted),
      (value) => isNil(value) || value?.length === 0,
    );

    return clean;
  }, [useSearchQuery, formatDates, formatParamsForQuery, formatParams]);

  // Formatted Search Params to be used for requests and query string
  const formatted = useMemo(() => formatSearchParams(searchParams), [searchParams]);
  const formattedForQuery = useMemo(() => formatSearchParamsForQuery(searchParams), [searchParams]);
  const formattedInitial = useMemo(() => (initialValues ? formatParams(initialValues) : {}), [initialValues]);

  // Check ALL required Search Params has been set
  const searchParamNames = Object.keys(omit(initialValues, ['deletedOnly']) ?? {});

  const hasSearchParams = isReady && searchParams && Object.keys(searchParams)
    .filter((paramKey) => searchParamNames.includes(paramKey))
    .length === searchParamNames.length;

  // Update query string when formatted params are changed
  useEffect(() => {
    if (!isReady || !useSearchQuery) {
      return;
    }
    if (formattedForQuery && useSearchQuery) {
      const queryString = getQueryParams(formattedForQuery);

      if (location.search !== queryString) {
        // If the order's been changed only - ignore
        const beforeObj = getSearchStringObject(location.search);
        const afterObj = getSearchStringObject(queryString);

        if (isEqual(beforeObj, afterObj)) {
          return;
        }

        history.replace(queryString === '' ? location.pathname : queryString);
      }
    }
  }, [formattedForQuery, isReady, useSearchQuery]);

  return {
    // Current not formatted state
    searchParams,
    hasSearchParams,
    // Current formatted Search params
    formatted,
    formattedInitial,
    // Handlers
    setSearchParams,
    setByKey,
    set,
    reset,
    // Recycle Bin related params
    recycleBin: searchParams[RECYCLE_BIN_PARAM]?.toString().toLowerCase() === 'true',
    showRecycleBin,
    hideRecycleBin,
  };
};

/**
 * Use for readonly search params as opposed to two-way bound to state bound like below
 * @returns {Object}
 */
export const useGetSearchParams = () => {
  const location = useLocation();
  // Get params from Query
  const queryStringParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  return queryStringParams || {};
};

/**
 * Use to push state changes to url
 * @returns {Object}
 */
export const useUrlBoundSearchParams = (...args) => {
  const location = useLocation();
  const history = useHistory();
  const state = useSearchParams(...args);
  React.useEffect(() => {
    history.push({ pathname: location.pathname, search: `?${ qs.stringify(state.searchParams)}` });
  }, [state.searchParams]);
  return state;
};

export default useSearchParams;